import { useQuery } from '@tanstack/react-query';
import { api } from '../client';
import { useAuthStore } from '../../store/authStore';

/**
 * Resposta do endpoint /risco-evasao
 */
interface AlunoEmRisco {
  aluno: { id: string; nome: string; matricula: string | null; fotoUrl: string | null };
  turma: { id: string; nome: string; serie: string; turno: string } | null;
  nivel: 'ATENCAO' | 'ALERTA' | 'CRITICO' | 'EVASAO';
  tipoIncidente: string;
  severidade: string;
  descricao: string | null;
  dataDeteccao: string;
}

interface ResumoRisco {
  total: number;
  atencao: number;
  alerta: number;
  critico: number;
  evasao: number;
}

interface RiscoEvasaoResponse {
  resumo: ResumoRisco;
  alunos: AlunoEmRisco[];
}

/**
 * Hook para consultar alunos em risco de evasão.
 * GET /escolas/:escolaId/incidentes-operacionais/risco-evasao
 *
 * @param filters.nivel - Filtrar por nível: ATENCAO | ALERTA | CRITICO | EVASAO
 * @param filters.turmaId - Filtrar por turma
 */
export function useRiscoEvasao(filters?: { nivel?: string; turmaId?: string }) {
  const escolaId = useAuthStore((s) => s.escolaId);

  const query = useQuery({
    queryKey: ['risco-evasao', escolaId, filters],
    queryFn: async () => {
      const { data } = await api.get<RiscoEvasaoResponse>(
        `/escolas/${escolaId}/incidentes-operacionais/risco-evasao`,
        { params: filters },
      );
      return data;
    },
    enabled: !!escolaId,
    refetchInterval: 5 * 60_000, // Atualiza a cada 5 min
  });

  return {
    resumo: query.data?.resumo ?? { total: 0, atencao: 0, alerta: 0, critico: 0, evasao: 0 },
    alunos: query.data?.alunos ?? [],
    isLoading: query.isLoading,
    error: query.error,
  };
}

/** Incidente de evasão aberto para um aluno (filtro client-side na lista da escola/turma). */
export function useRiscoAluno(alunoId?: string, turmaId?: string) {
  const { alunos, isLoading } = useRiscoEvasao(turmaId ? { turmaId } : undefined);
  const registro = alunos.find((a) => a.aluno.id === alunoId) ?? null;
  return { registro, isLoading };
}
